import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowLeft, BookOpen, TrendingUp, Calculator, Target, ChartBar } from 'lucide-react';
import SEOHead from '../../../components/SEO/SEOHead';
import SEOFooter from '../../../components/SEO/SEOFooter';

const GuiaApostas = () => {
  const [expandedSections, setExpandedSections] = React.useState({});

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Structured data for SEO
  const structuredData = {
    article: {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": "Guia Completo de Apostas Esportivas com IA - Estratégias de Apostas | PickStar AI",
      "description": "Domine as apostas esportivas com insights de IA. Aprenda o básico das apostas, gestão de banca, estratégias vencedoras e como o PickStar AI te dá vantagem.",
      "keywords": "guia apostas esportivas, apostas com IA, estratégias de apostas, gestão de banca, dicas apostas esportivas, odds explicadas, previsões IA, PickStar AI Brasil, apostas para iniciantes, estratégias profissionais apostas",
      "author": {
        "@type": "Organization",
        "name": "PickStar AI Brasil",
        "url": "https://pickstarai.com/pt-br"
      },
      "publisher": {
        "@type": "Organization",
        "name": "PickStar AI",
        "logo": {
          "@type": "ImageObject",
          "url": "https://pickstarai.com/logo.png"
        }
      },
    "datePublished": "2025-08-22",
    "dateModified": new Date().toISOString(),
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": "https://pickstarai.com/pt-br/resources/guia-apostas"
      },
      "image": "https://pickstarai.com/images/guia-apostas-hero.jpg",
      "inLanguage": "pt-BR"
    },
    howTo: {
      "@context": "https://schema.org",
      "@type": "HowTo",
      "name": "Como Usar IA para Apostas Esportivas",
      "description": "Guia passo a passo para usar o PickStar AI para tomar decisões mais inteligentes em apostas esportivas",
      "inLanguage": "pt-BR",
      "step": [
        {
          "@type": "HowToStep",
          "name": "Faça Perguntas Específicas",
          "text": "Faça perguntas específicas ao PickStar AI sobre jogos, times ou oportunidades de apostas"
        },
        {
          "@type": "HowToStep",
          "name": "Revise a Análise da IA",
          "text": "Revise a análise detalhada da IA incluindo estatísticas, tendências e níveis de confiança"
        },
        {
          "@type": "HowToStep",
          "name": "Compare com Sua Pesquisa",
          "text": "Use previsões de IA junto com seu próprio conhecimento e outras fontes confiáveis"
        },
        {
          "@type": "HowToStep",
          "name": "Faça Apostas Inteligentes",
          "text": "Siga regras de gestão de banca e aposte responsavelmente baseado em insights combinados"
        }
      ]
    },
    faq: {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "O que torna o PickStar AI melhor que o ChatGPT para apostas esportivas?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "O PickStar AI é construído por engenheiros de machine learning que também são apostadores profissionais. Nossa engenharia de contexto especializada entende nuances de apostas que modelos de IA genéricos perdem, fornecendo previsões superiores e inteligência de mercado em tempo real."
          }
        },
        {
          "@type": "Question",
          "name": "O que é a regra de gestão de banca de 1-3%?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "A regra de 1-3% significa nunca arriscar mais de 1-3% de sua banca total em uma única aposta. Por exemplo, com uma banca de R$1.000, sua aposta máxima deve ser de R$10-R$30 por aposta. Isso protege você de perdas devastadoras."
          }
        },
        {
          "@type": "Question",
          "name": "Quais são os principais tipos de apostas esportivas?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Os principais tipos são: Moneyline (escolher o vencedor), Handicap (apostar na margem de vitória), Over/Under (total de pontos marcados), Props (eventos específicos do jogador/jogo) e Parlays (múltiplas apostas combinadas para pagamentos maiores)."
          }
        },
        {
          "@type": "Question",
          "name": "Como funcionam as odds americanas?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "As odds americanas mostram o lucro potencial. Odds positivas (+150) significa apostar R$100 para ganhar R$150. Odds negativas (-150) significa apostar R$150 para ganhar R$100. Os favoritos têm odds negativas, azarões têm odds positivas."
          }
        }
      ]
    }
  };

  return (
    <>
      <Helmet>
        <title>Guia de Apostas Esportivas 2024 - Estratégias com IA | PickStar AI Brasil</title>
        <meta name="description" content="Domine apostas esportivas com IA em 2024. Guia completo sobre básico das apostas, gestão de banca, estratégias vencedoras e como usar PickStar AI para apostas mais inteligentes." />
        <meta name="keywords" content="guia apostas esportivas, apostas com IA Brasil, estratégias apostas 2024, gestão de banca, dicas apostas esportivas, odds explicadas, previsões IA, PickStar AI Brasil, apostas Brasileirão, apostas Copa do Brasil" />
        <link rel="canonical" href="https://pickstarai.com/pt-br/resources/guia-apostas" />
        <meta name="language" content="Portuguese" />
        <meta name="content-language" content="pt-BR" />
        
        {/* Open Graph tags */}
        <meta property="og:title" content="Guia Completo de Apostas Esportivas com IA - PickStar AI Brasil" />
        <meta property="og:description" content="Aprenda a apostar com inteligência usando IA. Domine o básico, estratégias e gestão de banca." />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://pickstarai.com/pt-br/resources/guia-apostas" />
        <meta property="og:image" content="https://pickstarai.com/images/guia-apostas-og.jpg" />
        <meta property="og:locale" content="pt_BR" />
        
        {/* Twitter Card tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Guia de Apostas Esportivas com IA | PickStar AI Brasil" />
        <meta name="twitter:description" content="Domine apostas esportivas com previsões e estratégias de IA" />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData.article)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(structuredData.howTo)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(structuredData.faq)}
        </script>
      </Helmet>
      
      <SEOHead 
        customTitle="Guia de Apostas Esportivas - Aprenda com IA | PickStar AI"
        customDescription="Guia completo de apostas esportivas com IA. Aprenda o básico das apostas, estratégias, gestão de banca e como usar previsões de IA para apostas mais inteligentes."
      />
      
      <div className="min-h-screen bg-black">
        {/* Header - Terminal style with glass */}
        <header className="fixed top-0 z-50 w-full bg-black/90 backdrop-blur-md border-b border-white/[0.05]">
          <div className="max-w-screen-xl mx-auto px-6 py-4">
            <Link to="/" className="inline-flex items-center gap-2 text-white/40 hover:text-emerald-400/70 transition-colors duration-200">
              <ArrowLeft className="w-4 h-4" />
              <span className="text-[11px] font-medium tracking-[0.2em] uppercase">Voltar</span>
            </Link>
          </div>
        </header>

        {/* Hero Section - Terminal typography with glass depth */}
        <section className="relative pt-24 pb-16 px-6" role="banner" aria-label="Cabeçalho da página">
          {/* Glass background layer */}
          <div className="absolute inset-0 bg-gradient-to-b from-white/[0.02] to-transparent backdrop-blur-sm" />

          <div className="relative max-w-screen-xl mx-auto">
            <div className="mb-6">
              <span className="inline-block w-2 h-2 bg-emerald-500/70 rounded-full animate-pulse mr-3" />
              <span className="text-[10px] font-medium tracking-[0.3em] text-white/30 uppercase">
                Guia Completo
              </span>
            </div>
            <h1 className="text-5xl lg:text-7xl font-thin text-white/95 mb-4">
              <span className="block">Apostas Esportivas</span>
              <span className="block text-3xl lg:text-5xl text-white/70">com Inteligência Artificial</span>
            </h1>
            <p className="text-sm text-white/50 max-w-2xl">
              Domine os fundamentos das apostas esportivas com insights de IA de nível profissional
            </p>
          </div>
        </section>

        {/* Main Content */}
        <main className="px-6 pb-24" role="main" aria-label="Conteúdo principal">
          <div className="max-w-screen-xl mx-auto">

            {/* Introduction - Glass card with terminal header */}
            <section id="introducao" className="mb-8 bg-white/[0.02] backdrop-blur-sm border border-white/[0.05] rounded-lg p-6 lg:p-8 hover:bg-white/[0.03] transition-all duration-200" aria-labelledby="intro-heading">
              <h2 className="text-[11px] font-medium tracking-[0.25em] text-white/30 uppercase mb-6">
                Introdução
              </h2>
              <div className="space-y-4">
                <p className="text-sm text-white/70 leading-relaxed">
                  Bem-vindo ao futuro das apostas esportivas. O PickStar AI combina inteligência artificial
                  de ponta com dados esportivos abrangentes para dar a você uma vantagem sem precedentes
                  em suas decisões de apostas.
                </p>
                <p className="text-sm text-white/50 leading-relaxed">
                  Este guia irá ensiná-lo os fundamentos das apostas esportivas, como aproveitar previsões
                  de IA e estratégias para maximizar seu sucesso mantendo práticas responsáveis de jogo.
                </p>
              </div>
            </section>

            {/* Betting Basics - Terminal data grid with glass */}
            <section id="basico-apostas" className="mb-8 bg-white/[0.02] backdrop-blur-sm border border-white/[0.05] rounded-lg overflow-hidden" aria-labelledby="basics-heading">
              <div className="bg-gradient-to-r from-emerald-500/[0.02] to-violet-500/[0.02] border-b border-white/[0.05] px-6 lg:px-8 py-4">
                <div className="flex items-center gap-3">
                  <Target className="w-4 h-4 text-emerald-400/70" />
                  <h2 id="basics-heading" className="text-[11px] font-medium tracking-[0.25em] text-white/30 uppercase">
                    Básico das Apostas
                  </h2>
                </div>
              </div>
              
              <div className="p-6 lg:p-8 space-y-8">
                <div>
                  <h3 className="text-xs font-medium tracking-[0.2em] text-emerald-400/70 uppercase mb-4">
                    Entendendo as Odds
                  </h3>
                  <p className="text-sm text-white/50 mb-4">
                    As odds representam a probabilidade de um resultado e determinam seu pagamento potencial:
                  </p>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="bg-black/30 backdrop-blur-sm border border-white/[0.03] rounded p-4 hover:border-emerald-500/20 transition-all duration-200">
                      <span className="block text-[10px] tracking-[0.2em] text-white/30 uppercase mb-2">Americanas</span>
                      <span className="block font-mono text-emerald-400/80 text-lg mb-2">+150 / -150</span>
                      <span className="text-xs text-white/50">Apostar R$100 para ganhar R$150</span>
                    </div>
                    <div className="bg-black/30 backdrop-blur-sm border border-white/[0.03] rounded p-4 hover:border-emerald-500/20 transition-all duration-200">
                      <span className="block text-[10px] tracking-[0.2em] text-white/30 uppercase mb-2">Decimais</span>
                      <span className="block font-mono text-emerald-400/80 text-lg mb-2">2.50</span>
                      <span className="text-xs text-white/50">Retorno de R$2,50 por R$1</span>
                    </div>
                    <div className="bg-black/30 backdrop-blur-sm border border-white/[0.03] rounded p-4 hover:border-emerald-500/20 transition-all duration-200">
                      <span className="block text-[10px] tracking-[0.2em] text-white/30 uppercase mb-2">Fracionárias</span>
                      <span className="block font-mono text-emerald-400/80 text-lg mb-2">3/2</span>
                      <span className="text-xs text-white/50">Ganhar R$3 para cada R$2</span>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-xs font-medium tracking-[0.2em] text-emerald-400/70 uppercase mb-4">
                    Tipos de Apostas
                  </h3>
                  <div className="divide-y divide-white/[0.02]">
                    {[
                      { name: 'Moneyline', desc: 'Escolher o vencedor do jogo', odds: '1.85' },
                      { name: 'Handicap', desc: 'Apostar na margem de vitória', odds: '1.90' },
                      { name: 'Over/Under', desc: 'Total de pontos marcados', odds: '1.95' },
                      { name: 'Props', desc: 'Eventos específicos do jogador', odds: '2.20' },
                      { name: 'Parlays', desc: 'Múltiplas apostas combinadas', odds: '5.50' }
                    ].map((type, idx) => (
                      <div key={idx} className="grid grid-cols-12 gap-4 py-3 hover:bg-white/[0.02] transition-colors group">
                        <span className="col-span-3 text-sm text-white/70">{type.name}</span>
                        <span className="col-span-6 text-xs text-white/50">{type.desc}</span>
                        <span className="col-span-3 font-mono text-sm text-violet-400/70 group-hover:text-violet-400/90 text-right">
                          {type.odds}×
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </section>

            {/* AI Advantage - Premium glass cards */}
            <section id="vantagem-ia" className="mb-8" aria-labelledby="ai-heading">
              <div className="mb-6 pb-4 border-b border-white/[0.05]">
                <div className="flex items-center gap-3">
                  <TrendingUp className="w-4 h-4 text-violet-400/70" />
                  <h2 id="ai-heading" className="text-[11px] font-medium tracking-[0.25em] text-white/30 uppercase">
                    Vantagem PickStar AI
                  </h2>
                </div>
              </div>

              <div className="mb-6">
                <p className="text-sm text-white/70 max-w-3xl">
                  Diferente de IA genérica como ChatGPT, a PickStar AI é construída por engenheiros de machine learning altamente treinados que também são
                  especialistas em apostas esportivas com anos de experiência profissional.
                </p>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  {
                    title: 'Engenharia Especializada',
                    value: '98.7%',
                    desc: 'Precisão superior ao ChatGPT em apostas'
                  },
                  {
                    title: 'Contexto Profissional',
                    value: '24/7',
                    desc: 'Monitoramento de mercado em tempo real'
                  },
                  {
                    title: 'Machine Learning',
                    value: '150K+',
                    desc: 'Partidas analisadas mensalmente'
                  },
                  {
                    title: 'Taxa de Sucesso',
                    value: '72.3%',
                    desc: 'Em apostas de alta confiança'
                  }
                ].map((item, idx) => (
                  <div key={idx} className="relative bg-white/[0.02] backdrop-blur-sm border border-white/[0.05] rounded-lg p-6 hover:bg-white/[0.03] hover:border-violet-500/20 transition-all duration-200 group">
                    <div className="absolute top-4 right-4">
                      <span className="w-1.5 h-1.5 bg-emerald-500/70 rounded-full animate-pulse" />
                    </div>
                    <h4 className="text-[10px] font-medium tracking-[0.2em] text-white/30 uppercase mb-3">
                      {item.title}
                    </h4>
                    <div className="font-mono text-2xl text-emerald-400/80 group-hover:text-emerald-400/90 mb-2">
                      {item.value}
                    </div>
                    <p className="text-[11px] text-white/40">
                      {item.desc}
                    </p>
                  </div>
                ))}
              </div>
            </section>

            {/* Bankroll Management - Highlighted glass card */}
            <section id="gestao-banca" className="mb-8 relative" aria-labelledby="bankroll-heading">
              {/* Glow effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/5 to-violet-500/5 blur-xl" />

              <div className="relative bg-gradient-to-r from-emerald-500/[0.02] to-violet-500/[0.02] backdrop-blur-sm border border-emerald-500/20 rounded-lg p-6 lg:p-8">
                <div className="flex items-center gap-3 mb-6">
                  <Calculator className="w-4 h-4 text-emerald-400/70" />
                  <h2 id="bankroll-heading" className="text-[11px] font-medium tracking-[0.25em] text-white/30 uppercase">
                    Gestão de Banca
                  </h2>
                </div>

                <div className="bg-black/30 backdrop-blur-sm border border-emerald-500/10 rounded-lg p-6 mb-6">
                  <h3 className="text-xs font-medium tracking-[0.2em] text-emerald-400/70 uppercase mb-4">
                    A Regra de Ouro: 1-3%
                  </h3>
                  <p className="text-sm text-white/70 mb-4">
                    Nunca arrisque mais de 1-3% de sua banca total em uma única aposta.
                  </p>
                  <div className="bg-black/50 rounded p-4 border border-white/[0.03]">
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <span className="block text-[10px] tracking-wider text-white/30 uppercase mb-1">Banca</span>
                        <span className="font-mono text-lg text-white/80">R$1.000</span>
                      </div>
                      <div>
                        <span className="block text-[10px] tracking-wider text-white/30 uppercase mb-1">Aposta Min</span>
                        <span className="font-mono text-lg text-emerald-400/80">R$10</span>
                      </div>
                      <div>
                        <span className="block text-[10px] tracking-wider text-white/30 uppercase mb-1">Aposta Max</span>
                        <span className="font-mono text-lg text-violet-400/70">R$30</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-xs font-medium tracking-[0.2em] text-white/30 uppercase mb-4">
                    Princípios Fundamentais
                  </h3>
                  <ul className="space-y-2">
                    {[
                      'Defina uma banca dedicada separada das despesas',
                      'Registre todas as apostas e mantenha registros',
                      'Ajuste apostas baseado no crescimento da banca',
                      'Nunca persiga perdas com apostas maiores',
                      'Faça pausas durante sequências negativas'
                    ].map((item, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <span className="text-emerald-400/50 mt-0.5">•</span>
                        <span className="text-sm text-white/50">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </section>

            {/* Strategy Tips - Terminal data table */}
            <section id="estrategias-vencedoras" className="mb-8 bg-white/[0.02] backdrop-blur-sm border border-white/[0.05] rounded-lg overflow-hidden" aria-labelledby="strategy-heading">
              <div className="bg-gradient-to-r from-emerald-500/[0.02] to-violet-500/[0.02] border-b border-white/[0.05] px-6 lg:px-8 py-4">
                <div className="flex items-center gap-3">
                  <ChartBar className="w-4 h-4 text-violet-400/70" />
                  <h2 id="strategy-heading" className="text-[11px] font-medium tracking-[0.25em] text-white/30 uppercase">
                    Estratégias Vencedoras
                  </h2>
                </div>
              </div>

              <div className="grid md:grid-cols-2">
                <div className="p-6 lg:p-8 border-r border-white/[0.05]">
                  <h3 className="text-xs font-medium tracking-[0.2em] text-emerald-400/70 uppercase mb-4">
                    Práticas Recomendadas
                  </h3>
                  <ul className="space-y-3">
                    {[
                      'Compare odds entre várias casas',
                      'Foque em esportes que você entende',
                      'Use IA como parte da pesquisa',
                      'Mantenha registros detalhados',
                      'Defina limites de ganho/perda'
                    ].map((item, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <span className="text-emerald-400/70 text-xs mt-0.5">✓</span>
                        <span className="text-sm text-white/70">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="p-6 lg:p-8">
                  <h3 className="text-xs font-medium tracking-[0.2em] text-red-400/70 uppercase mb-4">
                    Evitar Sempre
                  </h3>
                  <ul className="space-y-3">
                    {[
                      'Apostar dinheiro que não pode perder',
                      'Perseguir perdas com apostas maiores',
                      'Apostar quando emocional',
                      'Ignorar gestão de banca',
                      'Confiar apenas na intuição'
                    ].map((item, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <span className="text-red-400/70 text-xs mt-0.5">✗</span>
                        <span className="text-sm text-white/70">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </section>

            {/* Using PickStar AI - Steps with glass */}
            <section id="como-usar" className="mb-8" aria-labelledby="howto-heading">
              <div className="mb-6 pb-4 border-b border-white/[0.05]">
                <h2 id="howto-heading" className="text-[11px] font-medium tracking-[0.25em] text-white/30 uppercase">
                  Como Usar PickStar AI
                </h2>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  {
                    step: '01',
                    title: 'Perguntas Específicas',
                    desc: 'Faça perguntas sobre jogos, times ou oportunidades'
                  },
                  {
                    step: '02',
                    title: 'Análise da IA',
                    desc: 'Revise estatísticas, tendências e confiança'
                  },
                  {
                    step: '03',
                    title: 'Compare Pesquisa',
                    desc: 'Use IA junto com outras fontes confiáveis'
                  },
                  {
                    step: '04',
                    title: 'Apostas Inteligentes',
                    desc: 'Siga gestão de banca e aposte responsavelmente'
                  }
                ].map((item, idx) => (
                  <div key={idx} className="relative bg-white/[0.02] backdrop-blur-sm border border-white/[0.05] rounded-lg p-6 hover:bg-white/[0.03] hover:border-violet-500/20 transition-all duration-200 group">
                    <div className="mb-4">
                      <span className="font-mono text-3xl text-white/10">{item.step}</span>
                    </div>
                    <h3 className="text-xs font-medium tracking-[0.15em] text-white/70 uppercase mb-2">
                      {item.title}
                    </h3>
                    <p className="text-xs text-white/40">
                      {item.desc}
                    </p>
                  </div>
                ))}
              </div>
            </section>

            {/* FAQ Section - Terminal accordions with glass */}
            <section id="perguntas-frequentes" className="mb-8" aria-labelledby="faq-heading">
              <div className="mb-6 pb-4 border-b border-white/[0.05]">
                <h2 id="faq-heading" className="text-[11px] font-medium tracking-[0.25em] text-white/30 uppercase">
                  Perguntas Frequentes
                </h2>
              </div>

              <div className="space-y-2">
                {[
                  {
                    q: 'O que torna o PickStar AI melhor que o ChatGPT?',
                    a: 'O PickStar AI é construído por engenheiros de ML que são apostadores profissionais. Nossa engenharia de contexto especializada entende nuances de apostas que modelos genéricos perdem.'
                  },
                  {
                    q: 'O que é a regra de gestão de banca de 1-3%?',
                    a: 'Significa nunca arriscar mais de 1-3% da banca total em uma aposta. Com R$1.000, aposte entre R$10-R$30 por aposta.'
                  },
                  {
                    q: 'Quais são os principais tipos de apostas?',
                    a: 'Moneyline (vencedor), Handicap (margem), Over/Under (total de pontos), Props (eventos específicos) e Parlays (múltiplas combinadas).'
                  },
                  {
                    q: 'Como funcionam as odds americanas?',
                    a: 'Odds positivas (+150) = apostar R$100 para ganhar R$150. Odds negativas (-150) = apostar R$150 para ganhar R$100.'
                  }
                ].map((faq, idx) => (
                  <div key={idx} className="bg-white/[0.02] backdrop-blur-sm border border-white/[0.05] rounded-lg overflow-hidden">
                    <button
                      onClick={() => toggleSection(`faq${idx}`)}
                      className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-white/[0.02] transition-all duration-200"
                      aria-expanded={expandedSections[`faq${idx}`]}
                    >
                      <h3 className="text-sm text-white/70">{faq.q}</h3>
                      <span className="font-mono text-xs text-emerald-400/50">
                        {expandedSections[`faq${idx}`] ? '−' : '+'}
                      </span>
                    </button>
                    {expandedSections[`faq${idx}`] && (
                      <div className="px-6 pb-4 border-t border-white/[0.03]">
                        <p className="text-sm text-white/50 pt-4">
                          {faq.a}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>
            
            {/* CTA Section - Gradient with glass */}
            <section className="text-center py-12" aria-label="Chamada para ação">
              <div className="relative inline-block">
                <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/20 to-violet-500/20 blur-xl" />
                <div className="relative bg-gradient-to-r from-white/[0.03] to-white/[0.02] backdrop-blur-sm border border-white/[0.05] rounded-lg p-8">
                  <h2 className="text-[11px] font-medium tracking-[0.25em] text-white/30 uppercase mb-4">
                    Pronto para Começar
                  </h2>
                  <p className="text-sm text-white/50 mb-6 max-w-md">
                    Junte-se a milhares de apostadores usando IA para decisões mais inteligentes
                  </p>
                  <Link
                    to="/chat"
                    className="inline-block px-8 py-3 bg-gradient-to-r from-emerald-500/80 to-violet-500/80
                             backdrop-blur-sm rounded-md text-white text-[11px] font-medium
                             tracking-[0.15em] uppercase hover:from-emerald-500 hover:to-violet-500
                             transition-all duration-200 shadow-lg shadow-emerald-500/10"
                  >
                    Conversar com IA
                  </Link>
                </div>
              </div>
            </section>
          </div>
        </main>

        {/* Footer */}
        <footer className="border-t border-white/[0.05] bg-black/50 backdrop-blur-sm">
          <div className="max-w-screen-xl mx-auto px-6 py-8">
            <p className="text-[10px] tracking-[0.2em] text-white/20 uppercase text-center">
              © 2024 PickStar AI • Aposte com Responsabilidade
            </p>
          </div>
        </footer>

        {/* SEO Footer */}
        <SEOFooter language="pt-BR" />
      </div>
    </>
  );
};

export default GuiaApostas;